import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Bot,
  FileCode,
  KeyRound,
  ScrollText,
  ServerCog,
  Shield,
  Sparkles,
  SquareTerminal,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';
import { SectionedPageLayout } from '@/components/layout/SectionedPageLayout';
import type { SectionedPageLayoutItem } from '@/components/layout/SectionedPageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  listAdminUsers,
  updateAdminUserRole,
  updateAdminUserActive,
  listAuditLog,
} from '@/lib/api';
import type { AuditLogEntry } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { EnterpriseApiKeysCard } from '@/components/admin/EnterpriseApiKeysCard';
import { AdminKillSwitchStatus } from '@/components/admin/AdminKillSwitchStatus';
import { AdminRenderCacheBackfillButton } from '@/components/admin/AdminRenderCacheBackfillButton';
import { AdminExportButton } from '@/components/admin/AdminExportButton';
import { AdminPublicationReconcilePanel } from '@/components/admin/AdminPublicationReconcilePanel';
import { AdminAssistantDefaultsCard } from '@/components/admin/AdminAssistantDefaultsCard';
import { CommandsPage } from '@/routes/CommandsPage';
import { ScriptsPage } from '@/routes/ScriptsPage';
import { SkillsPage } from '@/routes/SkillsPage';
import { McpConfigsPage } from '@/routes/McpConfigsPage';

type AdminSectionId =
  | 'overview'
  | 'members'
  | 'audit'
  | 'provider-keys'
  | 'assistant-defaults'
  | 'operations'
  | 'resources';

type ResourceKind = 'commands' | 'scripts' | 'skills' | 'mcp-configs';

const adminSections: readonly SectionedPageLayoutItem<AdminSectionId>[] = [
  { id: 'overview', label: 'Overview', icon: Shield, description: 'Access summary' },
  { id: 'members', label: 'Members', icon: Users, description: 'Roles and status' },
  { id: 'audit', label: 'Audit', icon: ScrollText, description: 'Recent changes' },
  { id: 'provider-keys', label: 'Provider Keys', icon: KeyRound, description: 'Enterprise keys' },
  {
    id: 'assistant-defaults',
    label: 'Assistant Defaults',
    icon: Bot,
    description: 'Global provider defaults',
  },
  { id: 'operations', label: 'Operations', icon: Activity, description: 'Platform controls' },
  { id: 'resources', label: 'Resources', icon: Wrench, description: 'Global assets' },
] as const;

const resourceKinds: readonly {
  id: ResourceKind;
  label: string;
  description: string;
  icon: typeof SquareTerminal;
}[] = [
  {
    id: 'commands',
    label: 'Commands',
    description: 'Slash command instruction files.',
    icon: SquareTerminal,
  },
  { id: 'scripts', label: 'Scripts', description: 'Reusable workflow scripts.', icon: FileCode },
  { id: 'skills', label: 'Skills', description: 'Agent skill packages.', icon: Sparkles },
  {
    id: 'mcp-configs',
    label: 'MCPs',
    description: 'Model Context Protocol configs.',
    icon: ServerCog,
  },
] as const;

function AuditTimestamp({ value }: { value: string }): React.ReactElement {
  const [label, setLabel] = useState('');
  useEffect(() => {
    setLabel(
      new Date(value).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    );
  }, [value]);
  return <>{label}</>;
}

function getActiveSection(pathname: string): AdminSectionId {
  if (pathname.startsWith('/admin/resources')) return 'resources';

  const section = pathname.split('/')[2];
  if (
    section === 'members' ||
    section === 'audit' ||
    section === 'provider-keys' ||
    section === 'assistant-defaults' ||
    section === 'operations'
  ) {
    return section;
  }
  return 'overview';
}

function getActiveResourceKind(pathname: string): ResourceKind | null {
  const kind = pathname.split('/')[3];
  if (kind === 'commands' || kind === 'scripts' || kind === 'skills' || kind === 'mcp-configs') {
    return kind;
  }
  return null;
}

export function AdminPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useSession();
  const [mutationError, setMutationError] = useState<string | null>(null);
  const activeSection = getActiveSection(location.pathname);
  const activeResourceKind = getActiveResourceKind(location.pathname);

  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: listAdminUsers,
  });

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['auditLog'],
    queryFn: () => listAuditLog({ limit: 100 }),
    enabled: activeSection === 'audit',
  });

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'user' | 'admin' }) =>
      updateAdminUserRole(userId, role),
    onSuccess: async () => {
      setMutationError(null);
      await queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
    },
    onError: err => {
      setMutationError(err instanceof Error ? err.message : 'Failed to update role');
    },
  });

  const toggleActive = useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      updateAdminUserActive(userId, active),
    onSuccess: async () => {
      setMutationError(null);
      await queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    },
    onError: err => {
      setMutationError(err instanceof Error ? err.message : 'Failed to update status');
    },
  });

  const adminCount = users?.filter(user => user.role === 'admin').length ?? 0;
  const activeCount = users?.filter(user => user.active).length ?? 0;

  return (
    <SectionedPageLayout
      title="Admin"
      description="Team, security, operations, and global resources."
      sidebarLabel="Admin"
      items={adminSections}
      activeId={activeSection}
      onActiveChange={section => {
        navigate(section === 'overview' ? '/admin' : `/admin/${section}`);
      }}
      contentClassName="bg-background"
    >
      {activeSection === 'overview' && (
        <OverviewSection
          userCount={users?.length ?? 0}
          adminCount={adminCount}
          activeCount={activeCount}
        />
      )}

      {activeSection === 'members' && (
        <Card>
          <CardHeader>
            <CardTitle>Members</CardTitle>
            <CardDescription>
              Promote or demote admins. The server blocks unsafe changes like demoting the last
              admin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mutationError && <p className="mb-4 text-sm text-destructive">{mutationError}</p>}
            {isLoading && <p className="text-sm text-muted-foreground">Loading members…</p>}
            {error && !isLoading && (
              <p className="text-sm text-destructive">
                {error instanceof Error ? error.message : 'Failed to load admin users'}
              </p>
            )}
            {!isLoading && !error && users && (
              <div className="overflow-hidden rounded-lg border border-border bg-surface">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-surface-inset text-left">
                    <tr>
                      <th className="px-4 py-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                        User
                      </th>
                      <th className="px-4 py-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                        Status
                      </th>
                      <th className="px-4 py-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                        Role
                      </th>
                      <th className="px-4 py-2.5 text-right font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => {
                      const nextRole = user.role === 'admin' ? 'user' : 'admin';
                      const isCurrentUser = user.id === currentUser?.id;
                      const isMutating =
                        updateRole.isPending && updateRole.variables?.userId === user.id;

                      return (
                        <tr key={user.id} className="border-t border-border">
                          <td className="px-4 py-3 align-top">
                            <div className="flex items-center gap-3">
                              <div
                                className="flex size-9 items-center justify-center rounded-full border-2 border-border-bright text-primary-foreground"
                                style={{
                                  background:
                                    'linear-gradient(135deg, var(--accent-bright), var(--flame))',
                                }}
                              >
                                <UserCog className="size-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-[13px] font-semibold text-text-primary">
                                    {user.githubLogin}
                                  </span>
                                  {isCurrentUser && (
                                    <span className="inline-flex items-center rounded-full border border-border bg-surface-elevated px-1.5 py-0 font-mono text-[9.5px] font-bold uppercase tracking-widest text-text-tertiary">
                                      You
                                    </span>
                                  )}
                                </div>
                                <div className="truncate font-mono text-[11px] text-text-tertiary">
                                  {user.email ?? 'No email on file'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top">
                            {user.active ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-[oklch(0.72_0.16_155/0.25)] bg-[oklch(0.72_0.16_155/0.12)] px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-success">
                                <span className="size-1.5 rounded-full bg-success" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                                <span className="size-1.5 rounded-full bg-text-tertiary" />
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            {user.role === 'admin' ? (
                              <span className="inline-flex items-center rounded-full border border-flame/25 bg-flame-bg-soft px-2 py-0.5 font-mono text-[10.5px] font-bold uppercase tracking-widest text-flame-hi">
                                admin
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full border border-border bg-surface-elevated px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-secondary">
                                {user.role}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant={user.active ? 'outline' : 'secondary'}
                                size="sm"
                                disabled={
                                  toggleActive.isPending &&
                                  toggleActive.variables?.userId === user.id
                                }
                                onClick={() => {
                                  toggleActive.mutate({
                                    userId: user.id,
                                    active: !user.active,
                                  });
                                }}
                              >
                                {toggleActive.isPending &&
                                toggleActive.variables?.userId === user.id
                                  ? 'Saving…'
                                  : user.active
                                    ? 'Deactivate'
                                    : 'Reactivate'}
                              </Button>
                              <Button
                                variant={user.role === 'admin' ? 'outline' : 'default'}
                                size="sm"
                                disabled={isMutating}
                                onClick={() => {
                                  updateRole.mutate({ userId: user.id, role: nextRole });
                                }}
                              >
                                {isMutating
                                  ? 'Saving…'
                                  : user.role === 'admin'
                                    ? 'Remove admin'
                                    : 'Make admin'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeSection === 'audit' && (
        <AuditLogPanel entries={auditData?.entries ?? []} isLoading={auditLoading} />
      )}

      {activeSection === 'provider-keys' && <EnterpriseApiKeysCard />}

      {activeSection === 'assistant-defaults' && <AdminAssistantDefaultsCard />}

      {activeSection === 'operations' && (
        <div className="flex flex-col gap-4">
          <AdminKillSwitchStatus />
          <AdminRenderCacheBackfillButton />
          <AdminExportButton />
          <AdminPublicationReconcilePanel />
        </div>
      )}

      {activeSection === 'resources' && <ResourcesSection activeKind={activeResourceKind} />}
    </SectionedPageLayout>
  );
}

function OverviewSection({
  userCount,
  adminCount,
  activeCount,
}: {
  userCount: number;
  adminCount: number;
  activeCount: number;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5 font-mono text-[10.5px] font-bold uppercase tracking-widest text-accent-bright">
          <Shield className="size-3" />
          Admin
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-text-primary">
          Team administration
        </h1>
        <p className="max-w-2xl text-sm text-text-tertiary">
          Manage members, audit activity, enterprise provider keys, operational controls, and global
          resources. Server-side auth still enforces all admin permissions.
        </p>
      </section>

      <section className="grid gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-border bg-surface p-4">
          <div className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-accent-bright" />
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
              Users
            </span>
          </div>
          <div className="mt-2 text-3xl font-semibold leading-none tracking-tight text-text-primary">
            {userCount}
          </div>
          <p className="mt-2 text-[11.5px] text-text-tertiary">
            Total teammates in the auth roster.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-surface p-4">
          <div className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-flame" />
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
              Admins
            </span>
          </div>
          <div className="mt-2 text-3xl font-semibold leading-none tracking-tight text-text-primary">
            {adminCount}
          </div>
          <p className="mt-2 text-[11.5px] text-text-tertiary">
            Accounts with{' '}
            <code className="rounded border border-border bg-surface-inset px-1 font-mono text-[10.5px] text-flame-hi">
              /admin
            </code>{' '}
            access.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-surface p-4">
          <div className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-success shadow-[0_0_6px_var(--success)]" />
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
              Active
            </span>
          </div>
          <div className="mt-2 text-3xl font-semibold leading-none tracking-tight text-text-primary">
            {activeCount}
          </div>
          <p className="mt-2 text-[11.5px] text-text-tertiary">
            Users currently allowed to sign in.
          </p>
        </div>
      </section>
    </div>
  );
}

function ResourcesSection({ activeKind }: { activeKind: ResourceKind | null }): React.ReactElement {
  return (
    <div className="flex min-h-full flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {resourceKinds.map(resource => (
          <Button
            key={resource.id}
            asChild
            size="sm"
            variant={activeKind === resource.id ? 'default' : 'outline'}
          >
            <Link to={`/admin/resources/${resource.id}`}>
              <resource.icon className="size-4" />
              {resource.label}
            </Link>
          </Button>
        ))}
      </div>

      {!activeKind && (
        <section className="grid gap-3 md:grid-cols-2">
          {resourceKinds.map(resource => (
            <Card key={resource.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <resource.icon className="size-4 text-primary" />
                  {resource.label}
                </CardTitle>
                <CardDescription>{resource.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild size="sm" variant="outline">
                  <Link to={`/admin/resources/${resource.id}`}>Open</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      {activeKind === 'commands' && <CommandsPage />}
      {activeKind === 'scripts' && <ScriptsPage />}
      {activeKind === 'skills' && <SkillsPage />}
      {activeKind === 'mcp-configs' && <McpConfigsPage />}
    </div>
  );
}

function AuditLogPanel({
  entries,
  isLoading,
}: {
  entries: AuditLogEntry[];
  isLoading: boolean;
}): React.ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit log</CardTitle>
        <CardDescription>
          Recent ownership, visibility, and access changes across the platform.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">Loading audit log…</p>}
        {!isLoading && entries.length === 0 && (
          <p className="text-sm text-muted-foreground">No audit entries yet.</p>
        )}
        {!isLoading && entries.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted/40 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Resource</th>
                  <th className="px-4 py-3 font-medium">Actor</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id} className="border-t border-border">
                    <td
                      suppressHydrationWarning
                      className="px-4 py-3 text-muted-foreground whitespace-nowrap"
                    >
                      <AuditTimestamp value={entry.created_at} />
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{entry.action.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <span className="text-xs">{entry.resource_type}</span>
                      {entry.resource_label ? (
                        <span className="ml-1 text-xs text-foreground">{entry.resource_label}</span>
                      ) : entry.resource_id ? (
                        <span
                          className="ml-1 text-xs font-mono text-muted-foreground/60"
                          title={entry.resource_id}
                        >
                          {entry.resource_id.slice(0, 8)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {entry.actor_label ? (
                        <span className="text-foreground">{entry.actor_label}</span>
                      ) : entry.actor_id ? (
                        <span className="font-mono" title={entry.actor_id}>
                          {entry.actor_id.slice(0, 8)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
